/**
 * Individual investor organization data builder utilities
 * Constructs complex organization mutation objects for create/update operations
 */

import { Bank } from "../../../types/org.type";
import {
  InvestmentMode,
  KYCStatus,
  Persona,
} from "@funded-here-interface/common/src/constant/enum";
import { Industry } from "../../../constants/enums";

/**
 * Bank state from Redux
 */
export interface IndividualInvestorBankState {
  bankName: string | null;
  accountNumber: string | null;
  accountType: string | null;
  swiftCode: string | null;
  accountHolderName: string | null;
  bankAddress: string | null;
  bankCountry: string | null;
  currency: string | null;
}

/**
 * Professional/Personal state from Redux
 */
export interface IndividualInvestorProfState {
  industryGroup: string | null;
  industry: string | null;
  industryOthers: string | null;
  occupation: string | null;
  experience: string | null;
  nationality: string | null;
  gender: string | null;
  employmentStatus: string | null;
  employmentPosition: string | null;
  dateOfBirth: string | null;
  countryOfBirth: string | null;
  countryOfResidence: string | null;
  address: string | null;
  postalCode: string | null;
  investmentAmount: number | null;
  phoneNumber: string | null;
  employerName: string | null;
  referralSource: string | null;
  otherReferral: string | null;
  numberOfTaxResidenceCountries: number | null;
  taxResidenceCountries: any;
  isPep: boolean | null;
}

/**
 * Documents upload interface for individual investors
 */
export interface IndividualInvestorDocumentsUpload {
  proofOfAddress: string[];
  salaryProof: string[];
  bankStatement: string[];
}

/**
 * Build Bank object from Redux state
 */
export const buildIndividualInvestorBankObject = (
  bankState: IndividualInvestorBankState
): Bank => {
  return {
    bankName: bankState.bankName as string,
    swiftCode: bankState.swiftCode as string,
    accountNo: bankState.accountNumber as string,
    accountType: (bankState.accountType as string).toLowerCase(),
    accountHolderName: bankState.accountHolderName as string,
    bankAddress: bankState.bankAddress as string,
    bankCountry: bankState.bankCountry as string,
    currency: bankState.currency as string,
  };
};

/**
 * Build investment preferences object
 */
export const buildIndividualInvestmentPreferences = (
  investmentAmount: number | null
) => {
  return {
    investmentSize: investmentAmount as number,
    investmentMode: InvestmentMode.MANUAL,
  };
};

/**
 * Base organization data shared between create and update
 */
export interface BaseIndividualInvestorOrgData {
  bank: Bank;
  token: string;
  companyPhoneNumber: string;
  onBoardingUserType: string;
  nameOfAuthorisedRepresentiative: string;
  occupation: string;
  numberofYears: number;
  legalEntityAddress: string;
  countryISOCode: string | null;
  countryOfIncorporation: string;
  industryGroup: string;
  industry: string;
  postalCode: string;
  nationality: string;
  gender: string;
  employmentStatus: string;
  employmentPosition: string;
  employerName: string;
  referralSource: string;
  dateOfBirth: string;
  countryOfBirth: string;
  documentsUpload: IndividualInvestorDocumentsUpload;
  investmentPreferences: { investmentSize: number; investmentMode: string };
  otherReferral: string;
  numberOfTaxResidenceCountries: number;
  taxResidenceCountries: any;
  isPep: boolean;
}

/**
 * Build base organization data object
 */
export const buildBaseIndividualInvestorOrgData = (params: {
  profState: IndividualInvestorProfState;
  bank: Bank;
  token: string;
  userFirstName: string;
  userLastName: string;
  countryCode: string | null;
  documentsUpload: IndividualInvestorDocumentsUpload;
  investmentPreferences: { investmentSize: number; investmentMode: string };
}): BaseIndividualInvestorOrgData => {
  const {
    profState,
    bank,
    token,
    userFirstName,
    userLastName,
    countryCode,
    documentsUpload,
    investmentPreferences,
  } = params;

  return {
    bank,
    token,
    companyPhoneNumber: profState.phoneNumber as string,
    onBoardingUserType: Persona.INVESTOR,
    nameOfAuthorisedRepresentiative: `${userFirstName} ${userLastName}`,
    occupation: profState.occupation as string,
    numberofYears: parseInt(profState.experience!),
    legalEntityAddress: profState.address as string,
    countryISOCode: countryCode,
    countryOfIncorporation: profState.countryOfResidence as string,
    industryGroup: profState.industryGroup as string,
    industry:
      profState.industry === Industry.OTHERS
        ? (profState.industryOthers as string)
        : (profState.industry as string),
    postalCode: `${profState.postalCode}`,
    nationality: profState.nationality as string,
    gender: profState.gender as string,
    employmentStatus: profState.employmentStatus as string,
    employmentPosition: profState.employmentPosition as string,
    employerName: profState.employerName as string,
    referralSource: profState.referralSource as string,
    dateOfBirth: profState.dateOfBirth as string,
    countryOfBirth: profState.countryOfBirth as string,
    documentsUpload,
    investmentPreferences,
    otherReferral: profState.otherReferral as string,
    numberOfTaxResidenceCountries: parseInt(
      `${profState.numberOfTaxResidenceCountries!}`
    ),
    taxResidenceCountries: profState.taxResidenceCountries!,
    // Default to false for existing users who don't have isPep field
    isPep: profState.isPep ?? false,
  };
};

/**
 * Build create organization data
 */
export const buildCreateIndividualInvestorOrgData = (params: {
  profState: IndividualInvestorProfState;
  bank: Bank;
  token: string;
  userFirstName: string;
  userLastName: string;
  countryCode: string | null;
  documentsUpload: IndividualInvestorDocumentsUpload;
  investmentPreferences: { investmentSize: number; investmentMode: string };
}) => {
  return buildBaseIndividualInvestorOrgData(params);
};

/**
 * Build update organization data
 */
export const buildUpdateIndividualInvestorOrgData = (params: {
  profState: IndividualInvestorProfState;
  bank: Bank;
  token: string;
  userFirstName: string;
  userLastName: string;
  countryCode: string | null;
  documentsUpload: IndividualInvestorDocumentsUpload;
  investmentPreferences: { investmentSize: number; investmentMode: string };
}) => {
  return {
    ...buildBaseIndividualInvestorOrgData(params),
    kycStatus: KYCStatus.PENDING_APPROVAL, // Update includes KYC status reset
  };
};
